import { LoanType } from "../../core/utils/types/loan-type";
import { ReservationType } from "../../core/utils/types/reservation-type";
import { DigitalProvider } from "../../core/utils/types/digital-provider";
import { PublizonIdentifier } from "../../core/utils/types/ids";

type AssetType = {
  src: string;
  type: "script" | "link";
  id: string;
};

export const playerAssets: AssetType[] = [
  {
    src: "https://play.pubhub.dk/1.3.0/js/player-kernel.min.js",
    type: "script",
    id: "player-kernel-js"
  }
];

export const readerAssets: AssetType[] = [
  {
    src: "https://reader.pubhub.dk/2.2.0/js/chunk-vendors.js",
    type: "script",
    id: "reader-chunk-vendors-js"
  },
  {
    src: "https://reader.pubhub.dk/2.2.0/js/app.js",
    type: "script",
    id: "reader-app-js"
  },
  {
    src: "https://reader.pubhub.dk/2.2.0/css/chunk-vendors.css",
    type: "link",
    id: "reader-chunk-vendors-css"
  },
  {
    src: "https://reader.pubhub.dk/2.2.0/css/app.css",
    type: "link",
    id: "reader-app-css"
  }
];

const appendedAssets = new Set<HTMLElement>();

export const appendAsset = ({ src, type, id }: AssetType) => {
  if (type === "script") {
    const scriptElement = document.createElement("script");
    scriptElement.src = src;
    scriptElement.defer = true;
    scriptElement.async = false;
    scriptElement.type = "module";
    scriptElement.id = id;
    document.head.appendChild(scriptElement);
    appendedAssets.add(scriptElement);
  }

  if (type === "link") {
    const linkElement = document.createElement("link");
    linkElement.href = src;
    linkElement.rel = "stylesheet";
    linkElement.id = id;
    document.head.appendChild(linkElement);
    appendedAssets.add(linkElement);
  }
};

export const removeAppendedAssets = () => {
  appendedAssets.forEach((element) => {
    if (document.head.contains(element)) {
      document.head.removeChild(element);
    }
    appendedAssets.delete(element);
  });
};

export const getOrderIdByIdentifier = ({
  loans,
  identifier
}: {
  loans: LoanType[];
  identifier: PublizonIdentifier;
}) => {
  const loanWithIdentifier = loans.find((i) => i.identifier === identifier);
  return loanWithIdentifier ? loanWithIdentifier.orderId : null;
};

/**
 * Where a digital loan opens. One url parameter per provider, because the two
 * readers do not recognise each other's keys: Publizon knows an order id, the
 * service layer a loan id.
 */
export const readerUrl = (id: string, provider?: DigitalProvider | null) => {
  const parameter = provider === "serviceLayer" ? "loanid" : "orderid";
  return new URL(
    `/reader?${parameter}=${encodeURIComponent(id)}`,
    window.location.href
  );
};

/**
 * Where a service layer audiobook loan plays.
 *
 * Only service layer loans have a page to play on - Publizon audiobooks play
 * in a modal, so no Publizon key ever links here.
 */
export const playerUrl = (loanId: string) =>
  new URL(`/player?loanid=${encodeURIComponent(loanId)}`, window.location.href);

/**
 * Whether a loan from this provider plays in the Publizon modal.
 *
 * Service layer loans play on the player page instead (see `playerUrl`), so a
 * caller that owns a `PlayerModal` only needs to mount it when this is true.
 */
export const playsInModal = (provider?: DigitalProvider | null) =>
  provider !== "serviceLayer";

/**
 * Where a sample of a material opens.
 *
 * A sample has no loan to read the material type from, so the route carries
 * it: e-books sample on the reader page, audiobooks on the player page.
 */
export const sampleUrl = (
  identifier: PublizonIdentifier,
  materialType: "ebook" | "audiobook"
) =>
  new URL(
    `/${materialType === "audiobook" ? "player" : "reader"}?identifier=${encodeURIComponent(identifier)}`,
    window.location.href
  );

export const findReservedReservation = (
  identifier: PublizonIdentifier,
  reservations: ReservationType[]
) =>
  reservations.find(
    (reservation) =>
      reservation.identifier === identifier && reservation.state === "reserved"
  );
